import type { GraphNode, NodeKind } from "../types/graph";

export type ColorMode = "kind" | "community";

export interface LegendEntry {
  label: string;
  color: string;
}

/**
 * Kind-based color palette mapping each NodeKind to a distinct hex color.
 */
const KIND_PALETTE: Record<NodeKind, string> = {
  Function: "#58a6ff",
  Class: "#d29922",
  Method: "#3fb950",
  Module: "#7d8590",
  Interface: "#a371f7",
  Enum: "#f778ba",
  Type: "#79c0ff",
  Route: "#f85149",
  Resource: "#d2a8ff",
  File: "#484f58",
};

/**
 * Neutral gray used for nodes with null community_id.
 */
const NEUTRAL_GRAY = "#7d8590";

/**
 * 20-hue categorical palette for community coloring.
 * Evenly spaced around the color wheel (HSL hue 0-360, step 18°),
 * with saturation 70% and lightness 55% for good visibility on dark backgrounds.
 */
const COMMUNITY_PALETTE: string[] = generateCommunityPalette(20);

function generateCommunityPalette(count: number): string[] {
  const colors: string[] = [];
  for (let i = 0; i < count; i++) {
    const hue = Math.round((360 / count) * i);
    colors.push(hslToHex(hue, 70, 55));
  }
  return colors;
}

/**
 * Convert HSL values to a hex color string.
 */
function hslToHex(h: number, s: number, l: number): string {
  const sNorm = s / 100;
  const lNorm = l / 100;

  const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lNorm - c / 2;

  let r = 0,
    g = 0,
    b = 0;

  if (h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    g = 0;
    b = c;
  } else {
    r = c;
    g = 0;
    b = x;
  }

  const toHex = (val: number): string => {
    const hex = Math.round((val + m) * 255).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Get the color for a node based on the active color mode.
 */
export function getNodeColor(node: GraphNode, mode: ColorMode): string {
  if (mode === "kind") {
    return KIND_PALETTE[node.kind] ?? NEUTRAL_GRAY;
  }
  // community mode
  return getCommunityColor(node.community_id);
}

/**
 * Get the full kind-based palette mapping.
 */
export function getKindPalette(): Record<string, string> {
  return { ...KIND_PALETTE };
}

/**
 * Get the color for a given community_id.
 * Returns neutral gray for null, otherwise maps via modulo into the 20-hue palette.
 */
export function getCommunityColor(communityId: number | null): string {
  if (communityId === null || communityId === undefined) {
    return NEUTRAL_GRAY;
  }
  const index = ((communityId % COMMUNITY_PALETTE.length) + COMMUNITY_PALETTE.length) % COMMUNITY_PALETTE.length;
  return COMMUNITY_PALETTE[index];
}

/**
 * Get legend entries for the active color mode.
 *
 * In "kind" mode: returns one entry per unique kind present in the nodes array.
 * In "community" mode: returns one entry per unique community_id present in the nodes array,
 * including an "Unassigned" entry for null community_id.
 */
export function getLegendEntries(mode: ColorMode, nodes: GraphNode[]): LegendEntry[] {
  if (mode === "kind") {
    const kindsPresent = new Set<NodeKind>();
    for (const node of nodes) {
      kindsPresent.add(node.kind);
    }
    const entries: LegendEntry[] = [];
    for (const kind of Object.keys(KIND_PALETTE) as NodeKind[]) {
      if (kindsPresent.has(kind)) {
        entries.push({ label: kind, color: KIND_PALETTE[kind] });
      }
    }
    return entries;
  }

  // community mode
  const communityIds = new Set<number | null>();
  for (const node of nodes) {
    communityIds.add(node.community_id);
  }

  const entries: LegendEntry[] = [];
  const sortedIds = Array.from(communityIds).sort((a, b) => {
    if (a === null) return 1;
    if (b === null) return -1;
    return a - b;
  });

  for (const id of sortedIds) {
    if (id === null) {
      entries.push({ label: "Unassigned", color: NEUTRAL_GRAY });
    } else {
      entries.push({ label: `Community ${id}`, color: getCommunityColor(id) });
    }
  }

  return entries;
}
